import { Injectable } from '@angular/core';
import { EsraPipingAPIClient, FileResponse } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getForbiddenMessage } from 'src/app/shared/utils';

@Injectable({
  providedIn: 'root',
})
export class PipingAndValvesDocumentService {
  constructor(
    private esraPipingAPIClient: EsraPipingAPIClient,
    private snackBarService: SnackBarService
  ) {}

  /**
   * Fetches the ESRA1 document for a pipe class or valve. PDFs open in a browser tab; any other
   * format (e.g. Word, Excel) is downloaded because browsers cannot display it.
   * The PDF search index's id maps directly to the ESRA1 record primary key, so no share-link
   * resolution step (name + business unit lookup) is needed -- the id from the search result
   * can be passed straight to getEsra1DocumentByRecordId.
   * @param id The pipe class or valve id, as returned by the PDF search results.
   * @param shareLinkTypeCode Share-link type prefix: 'pp' for piping material class, 'pv' for valve.
   * @param newWindow When true, opens in a new browser tab; otherwise replaces the current location.
   */
  public openPDF(id: string, shareLinkTypeCode: 'pp' | 'pv', newWindow = true): void {
    this.esraPipingAPIClient
      .getEsra1DocumentByRecordId(shareLinkTypeCode, id)
      .subscribe({
        next: (fileResponse: FileResponse) => this.openFileResponse(fileResponse, newWindow),
        error: (error: unknown) => {
          this.snackBarService.showError(getForbiddenMessage(error, 'Unable to retrieve document.'));
        },
      });
  }

  /**
   * Opens a fetched document: PDFs are shown in the browser, any other format is downloaded.
   * @param fileResponse The document returned by the API.
   * @param newWindow When true, PDFs open in a new tab; otherwise they replace the current location.
   * @returns `'opened'` when a PDF was shown in the browser, `'downloaded'` for any other format.
   */
  public openFileResponse(fileResponse: FileResponse, newWindow = true): 'opened' | 'downloaded' {
    const isPdf: boolean =
      fileResponse.data.type === 'application/pdf' || (fileResponse.fileName ?? '').toLowerCase().endsWith('.pdf');

    // Browsers can only render PDFs inline; any other format must be downloaded.
    if (!isPdf) {
      this.downloadFile(fileResponse);
      return 'downloaded';
    }

    const blob: Blob = new Blob([fileResponse.data], { type: 'application/pdf' });
    const url: string = window.URL.createObjectURL(blob);
    if (newWindow) {
      window.open(url);
    } else {
      window.location.replace(url);
    }
    return 'opened';
  }

  private downloadFile(fileResponse: FileResponse): void {
    const url: string = window.URL.createObjectURL(fileResponse.data);
    const anchor: HTMLAnchorElement = document.createElement('a');
    anchor.href = url;
    anchor.download = fileResponse.fileName ?? 'document';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }
}

